import { useState, useEffect } from "react";
import { MessageSquarePlus, Loader2, Bug, Lightbulb, MapPin, HelpCircle, Frown, Meh, Smile, Heart, ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { APP_VERSION_STRING } from "@/lib/version";

const enjoymentOptions = [
  { value: "not_great", icon: Frown, label: "Not great", color: "text-destructive", bgColor: "bg-destructive/10" },
  { value: "okay", icon: Meh, label: "It's okay", color: "text-honey", bgColor: "bg-honey/10" },
  { value: "good", icon: Smile, label: "Good!", color: "text-sage", bgColor: "bg-sage/10" },
  { value: "love_it", icon: Heart, label: "Love it!", color: "text-terracotta", bgColor: "bg-terracotta/10" },
];

interface FeedbackDialogProps {
  trigger?: React.ReactNode;
  defaultCategory?: "bug" | "feature" | "cafe_correction" | "other";
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSubmitSuccess?: () => void;
}

const categoryConfig = {
  bug: {
    icon: Bug,
    label: "Something's Off",
    description: "Something isn't working right",
    color: "text-destructive",
    bgColor: "bg-destructive/10",
  },
  feature: {
    icon: Lightbulb,
    label: "Idea or Wish",
    description: "Something you'd like to see",
    color: "text-honey",
    bgColor: "bg-honey/10",
  },
  cafe_correction: {
    icon: MapPin,
    label: "Cafe Update",
    description: "Update cafe info",
    color: "text-terracotta",
    bgColor: "bg-terracotta/10",
  },
  other: {
    icon: HelpCircle,
    label: "Just Sharing",
    description: "General thoughts",
    color: "text-sage",
    bgColor: "bg-sage/10",
  },
};

export function FeedbackDialog({ trigger, defaultCategory, open: controlledOpen, onOpenChange, onSubmitSuccess }: FeedbackDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const [enjoyment, setEnjoyment] = useState<string | null>(null);
  const [category, setCategory] = useState<keyof typeof categoryConfig>(defaultCategory || "feature");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  // Reset description expanded state when dialog closes
  useEffect(() => {
    if (!open) {
      setDescriptionExpanded(false);
    }
  }, [open]);

  const resetForm = () => {
    setEnjoyment(null);
    setCategory(defaultCategory || "feature");
    setSubject("");
    setDescription("");
    setDescriptionExpanded(false);
  };

  const handleSubmit = async () => {
    if (!subject.trim()) {
      toast({
        title: "Just need a few words",
        description: "Tell us briefly what's on your mind",
        variant: "destructive",
      });
      return;
    }

    // On mobile, description is optional - use subject as description if empty
    const finalDescription = description.trim() || subject.trim();

    setIsSubmitting(true);

    const { data: { session } } = await supabase.auth.getSession();

    const { error } = await supabase.from("app_feedback").insert({
      category,
      subject: subject.trim(),
      description: `${finalDescription}\n\nClient: ${APP_VERSION_STRING}`,
      user_id: session?.user?.id || null,
      status: "open",
      enjoyment: enjoyment,
    });

    setIsSubmitting(false);

    if (error) {
      console.error("Feedback error:", error);
      toast({
        title: "Submission failed",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Thanks for sharing! 💛",
      description: "Your feedback helps shape Grounded.",
    });

    setOpen(false);
    resetForm();
    onSubmitSuccess?.();
  };

  const CategoryButton = ({ cat }: { cat: keyof typeof categoryConfig }) => {
    const config = categoryConfig[cat];
    const Icon = config.icon;
    const isSelected = category === cat;

    return (
      <button
        type="button"
        onClick={() => setCategory(cat)}
        className={cn(
          "flex flex-col items-center gap-1.5 sm:gap-2 p-2 sm:p-3 rounded-lg border-2 transition-all min-h-[44px]",
          isSelected
            ? `border-primary ${config.bgColor}`
            : "border-border hover:border-primary/50 hover:bg-secondary/50"
        )}
      >
        <Icon className={cn("h-5 w-5", isSelected ? config.color : "text-muted-foreground")} />
        <span className={cn("text-[10px] sm:text-xs font-medium leading-tight text-center", isSelected ? "text-foreground" : "text-muted-foreground")}>
          {config.label}
        </span>
      </button>
    );
  };

  const EnjoymentButton = ({ option }: { option: typeof enjoymentOptions[0] }) => {
    const Icon = option.icon;
    const isSelected = enjoyment === option.value;
    
    return (
      <button
        type="button"
        onClick={() => setEnjoyment(option.value)}
        className={cn(
          "flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all min-h-[44px]",
          isSelected
            ? `border-primary ${option.bgColor}`
            : "border-border hover:border-primary/50 hover:bg-secondary/50"
        )}
      >
        <Icon className={cn("h-5 w-5", isSelected ? option.color : "text-muted-foreground")} />
        <span className={cn("text-[10px] font-medium leading-tight", isSelected ? "text-foreground" : "text-muted-foreground")}>
          {option.label}
        </span>
      </button>
    );
  };

  // If controlled externally (open prop provided), skip the trigger
  const isControlled = controlledOpen !== undefined;

  // Category Selection Component
  const CategorySection = (
    <div className="space-y-2">
      <Label>What's this about?</Label>
      <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
        {(Object.keys(categoryConfig) as Array<keyof typeof categoryConfig>).map((cat) => (
          <CategoryButton key={cat} cat={cat} />
        ))}
      </div>
    </div>
  );

  // Enjoyment Section Component
  const EnjoymentSection = (
    <div className="space-y-1.5">
      <Label className="text-muted-foreground text-sm">Quick vibe check (optional)</Label>
      <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
        {enjoymentOptions.map((option) => (
          <EnjoymentButton key={option.value} option={option} />
        ))}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          {trigger || (
            <Button variant="outline" size="sm" className="gap-2">
              <MessageSquarePlus className="h-4 w-4" />
              Send Feedback
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className={cn(
        "max-w-md",
        isMobile && "flex flex-col max-h-[85vh]"
      )}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="font-display">Share Your Thoughts</DialogTitle>
          <DialogDescription>
            We'd love to hear what you noticed — big or small
          </DialogDescription>
        </DialogHeader>

        <div className={cn(
          "space-y-4 sm:space-y-5 py-3 sm:py-4",
          isMobile && "flex-1 overflow-y-auto"
        )}>
          {/* On mobile: Category first (thumb reach), then vibe check */}
          {/* On desktop: Vibe check first, then category */}
          {isMobile ? (
            <>
              {CategorySection}
              {EnjoymentSection}
            </>
          ) : (
            <>
              {/* Enjoyment Question - Desktop */}
              <div className="space-y-2">
                <Label className="text-muted-foreground">Quick vibe check (optional)</Label>
                <p className="text-xs text-muted-foreground/70">Skip if you'd rather jump to feedback</p>
                <div className="grid grid-cols-4 gap-2">
                  {enjoymentOptions.map((option) => (
                    <EnjoymentButton key={option.value} option={option} />
                  ))}
                </div>
              </div>
              {CategorySection}
            </>
          )}

          {/* Subject - Primary input */}
          <div className="space-y-1.5">
            <Label htmlFor="feedback-subject">In a few words</Label>
            <Input
              id="feedback-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={
                category === "bug"
                  ? "e.g., The map was acting weird"
                  : category === "feature"
                  ? "e.g., Would be cool if..."
                  : category === "cafe_correction"
                  ? "e.g., Cafe hours are wrong"
                  : "e.g., Something I noticed"
              }
              maxLength={100}
              className="min-h-[44px]"
            />
            <p className="text-xs text-muted-foreground/70">
              One sentence is totally fine
            </p>
          </div>

          {/* Description - Collapsible on mobile */}
          {isMobile ? (
            <div className="space-y-1.5">
              <button
                type="button"
                onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors min-h-[44px] w-full justify-between"
              >
                <span>Add more details (optional)</span>
                <ChevronDown className={cn(
                  "h-4 w-4 transition-transform",
                  descriptionExpanded && "rotate-180"
                )} />
              </button>
              {descriptionExpanded && (
                <div className="space-y-1.5 animate-in slide-in-from-top-2 duration-200">
                  <Textarea
                    id="feedback-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Quick notes are okay — no need to be detailed"
                    rows={3}
                    maxLength={1000}
                    className="min-h-[80px]"
                  />
                  <p className="text-xs text-muted-foreground text-right">{description.length}/1000</p>
                </div>
              )}
            </div>
          ) : (
            /* Desktop: Always visible */
            <div className="space-y-2">
              <Label htmlFor="feedback-description">Tell us more</Label>
              <Textarea
                id="feedback-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={
                  category === "bug"
                    ? "Tell us what you noticed — don't worry about being technical"
                    : category === "feature"
                    ? "What felt missing or would make things better?"
                    : category === "cafe_correction"
                    ? "What needs updating? Even rough details help"
                    : "Anything that stood out as you used the app"
                }
                rows={4}
                maxLength={1000}
              />
              <p className="text-xs text-muted-foreground text-right">{description.length}/1000</p>
            </div>
          )}

          {/* Motivational Footer - Only on desktop or when not at bottom */}
          {!isMobile && (
            <p className="text-xs text-muted-foreground text-center pt-2">
              Early feedback helps shape what Grounded becomes.
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className={cn(
          "flex gap-3 flex-shrink-0",
          isMobile && "pt-3 pb-[env(safe-area-inset-bottom)] border-t border-border"
        )}>
          <Button variant="outline" className="flex-1 min-h-[44px]" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button className="flex-1 min-h-[44px]" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sharing...
              </>
            ) : (
              "Share Feedback"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
